import { Bookmark, BookmarkPlus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetIdentity, useListContext, useNotify } from "ra-core";
import { Button } from "@/components/ui/button";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

interface SavedView {
  id: string;
  name: string;
  params: {
    filter?: Record<string, unknown>;
    sort?: { field: string; order: "ASC" | "DESC" };
  };
  owner_id: number | null;
  shared: boolean;
}

/**
 * Named filter/sort presets for a list, private by default, shareable to the
 * workspace. Applying a view REPLACES the current filters, merging would
 * produce a state that is neither the view nor what the user had, which
 * nobody asked for.
 *
 * Only the owner can delete a view (enforced by RLS; the trash icon simply
 * is not rendered for the rest), a shared view anyone could delete stops
 * being a stable reference point.
 */
export const SavedViewsPicker = ({ resource }: { resource: string }) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const workspaceId = getActiveWorkspaceId();
  const { identity } = useGetIdentity();
  const { filterValues, sort, setFilters, setSort } = useListContext();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);

  const { data: views } = useQuery<SavedView[]>({
    queryKey: ["saved-views", workspaceId, resource],
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("saved_views")
        .select("id,name,params,owner_id,shared")
        .eq("workspace_id", workspaceId!)
        .eq("resource", resource)
        .order("name");
      if (error) throw new Error(error.message);
      return (data ?? []) as SavedView[];
    },
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["saved-views"] });

  const apply = (view: SavedView) => {
    setFilters(view.params.filter ?? {}, {});
    if (view.params.sort) setSort(view.params.sort);
  };

  const save = async () => {
    if (!name.trim()) {
      notify("Name the view first", { type: "warning" });
      return;
    }
    const { error } = await getSupabaseClient()
      .from("saved_views")
      .insert({
        workspace_id: workspaceId,
        resource,
        name: name.trim(),
        params: { filter: filterValues, sort },
        owner_id: identity?.id,
        shared,
      });
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify(shared ? "View saved and shared" : "View saved");
    setSaving(false);
    setName("");
    setShared(false);
    refresh();
  };

  const remove = async (view: SavedView) => {
    const { error } = await getSupabaseClient()
      .from("saved_views")
      .delete()
      .eq("id", view.id);
    if (error) notify(error.message, { type: "error" });
    refresh();
  };

  const list = views ?? [];

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {list.map((view) => (
        <span
          key={view.id}
          className="group flex items-center gap-0.5 rounded-full border border-border bg-card pl-2.5 text-[12px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <button
            type="button"
            className="flex items-center gap-1 py-1"
            onClick={() => apply(view)}
            title={view.shared ? "Shared with the workspace" : "Only you"}
          >
            <Bookmark className="size-3" />
            {view.name}
          </button>
          {view.owner_id === identity?.id ? (
            <button
              type="button"
              className="px-1.5 py-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
              onClick={() => remove(view)}
              aria-label={`Delete view ${view.name}`}
            >
              <Trash2 className="size-3" />
            </button>
          ) : (
            <span className="pr-2" />
          )}
        </span>
      ))}

      {saving ? (
        <span className="flex items-center gap-1.5">
          <input
            autoFocus
            className="h-7 rounded-md border border-input bg-transparent px-2 text-[12px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="View name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
          />
          <label className="flex items-center gap-1 text-[11.5px] text-muted-foreground">
            <input
              type="checkbox"
              checked={shared}
              onChange={(e) => setShared(e.target.checked)}
            />
            share
          </label>
          <Button size="sm" className="h-7" onClick={save}>
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7"
            onClick={() => setSaving(false)}
          >
            Cancel
          </Button>
        </span>
      ) : (
        <button
          type="button"
          className="flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-1 text-[12px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          onClick={() => setSaving(true)}
          title="Save the current filters and sort as a view"
        >
          <BookmarkPlus className="size-3" />
          Save view
        </button>
      )}
    </div>
  );
};
